/**
 * Wash Performance Report Page - 1:1 LEGACY PARITY
 *
 * Source of Truth: /opt/wash-ops/src/app/admin/wash-report/page.tsx
 *
 * Layout:
 * 1. Header: Title + Refresh/Export buttons
 * 2. Time Filter: Preset BUTTONS (not dropdown!) + custom range
 * 3. Hinweis, wenn eine Taktwaschanlage in Schichten keine Daten lieferte
 *
 * Eine Taktwaschanlage ohne Maschinendaten zählt mit 0 kg (Entscheidung
 * 19.10.2026, Wunsch ELIS: Stand die Anlage, ist das ein Nulltag). Bis dahin
 * setzte schon eine Schicht ohne P25 die Waschleistung des ganzen Zeitraums
 * auf „–". Die Lücke bleibt mit Sternchen und Hinweis sichtbar, weil sie auch
 * ein Datenausfall sein kann.
 * 4. KPI Cards: WSA, P25, P50, Waschleistung, Fertigleistung
 * 5. Table: Expandable days with shift details + SUMME row
 */

import { useState, useEffect, useCallback, useMemo, Fragment } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  RefreshCw,
  Download,
  FileSpreadsheet,
  AlertTriangle,
  TrendingUp,
  WashingMachine,
  Scale,
  ChevronDown,
  ChevronRight,
  Calendar,
  Info,
} from 'lucide-react';
import { AdminLayout } from '../components/AdminLayout';
import { ExportButtonsPlaceholder } from '../components/ExportButtonsPlaceholder';
import {
  getTodayBerlin,
  getQuickPickerRange,
  type QuickPicker,
} from '@wash-ops/shared';
import { formatBerlinDateDE } from '../utils/berlinDate';

// =============================================================================
// TYPES (1:1 Legacy)
// =============================================================================

type Preset = QuickPicker | 'custom';
type ShiftType = 'EARLY' | 'LATE' | 'SATURDAY';

interface ShiftReport {
  shiftDate: string;
  shiftType: ShiftType;
  wsaKg: number;
  flKg: number;
  p25: number;
  p50: number;
  wlKg: number;
  hbwKg: number;
  missingP25: boolean;
  missingP50: boolean;
}

interface DayReport {
  date: string;
  wsaKg: number;
  flKg: number;
  p25Kg: number;
  p50Kg: number;
  wlKg: number;
  hbwKg: number;
  shifts: ShiftReport[];
  missingP25: boolean;
  missingP50: boolean;
}

interface ReportTotals {
  wsaKg: number;
  flKg: number;
  p25Kg: number;
  p50Kg: number;
  wlKg: number;
  hbwKg: number;
  shiftsMissingP25: number;
  shiftsMissingP50: number;
}

interface ReportData {
  days: DayReport[];
  totals: ReportTotals;
  rangeLabel: string;
}

const SHIFT_LABELS: Record<ShiftType, string> = {
  EARLY: 'Frühschicht',
  LATE: 'Spätschicht',
  SATURDAY: 'Samstag',
};

const SHIFT_BADGE_CLASS: Record<ShiftType, string> = {
  EARLY: 'inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-blue-100 text-blue-700',
  LATE: 'inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-green-100 text-green-700',
  SATURDAY: 'inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-purple-100 text-purple-700',
};

const PRESET_LABELS: Record<Preset, string> = {
  today: 'Heute',
  yesterday: 'Gestern',
  dayBefore: 'Vorgestern',
  thisWeek: 'Diese Woche',
  lastWeek: 'Letzte Woche',
  thisMonth: 'Dieser Monat',
  lastMonth: 'Letzter Monat',
  custom: 'Zeitraum wählen',
};

/**
 * Formatiert einen YYYY-MM-DD String als DD.MM.YYYY
 */
function formatDateDE(dateStr: string): string {
  const [y, m, d] = dateStr.split('-');
  return `${d}.${m}.${y}`;
}

/**
 * Generiert ein konkretes Range-Label für die UI
 * Zeigt immer den effektiven Zeitraum mit Start–Ende
 */
function getRangeLabelConcrete(preset: Preset, startDate: string, endDate: string): string {
  const today = getTodayBerlin();
  const startDE = formatDateDE(startDate);
  const endDE = formatDateDE(endDate);
  
  switch (preset) {
    case 'today':
      return `${startDE} (bis jetzt)`;
    case 'yesterday':
    case 'dayBefore':
      return startDE;
    case 'thisWeek':
    case 'thisMonth':
      return `${startDE} \u2013 ${endDE} (bis heute)`;
    case 'lastWeek':
    case 'lastMonth':
      return `${startDE} \u2013 ${endDE}`;
    case 'custom':
      if (startDate === endDate) {
        return startDE;
      }
      if (endDate >= today) {
        return `${startDE} \u2013 ${endDE} (bis heute)`;
      }
      return `${startDE} \u2013 ${endDE}`;
    default:
      return `${startDE} \u2013 ${endDE}`;
  }
}

// =============================================================================
// DATE RANGE UTILS (uses Rebuild API with detail=shift)
// =============================================================================

interface DateRange {
  start: string;
  end: string;
  label: string;
  isPartial: boolean;
}

function computeRange(preset: Preset, customStart?: string, customEnd?: string): DateRange {
  if (preset === 'custom') {
    if (customStart && customEnd) {
      return {
        start: customStart,
        end: customEnd,
        label: `${formatDateShort(customStart)} – ${formatDateShort(customEnd)}`,
        isPartial: false,
      };
    }
    const today = getTodayBerlin();
    return { start: today, end: today, label: 'Bitte Zeitraum wählen', isPartial: false };
  }

  const range = getQuickPickerRange(preset);
  return {
    start: range.startDate,
    end: range.endDate,
    label: range.label,
    isPartial: range.isPartial,
  };
}

function formatDateShort(dateStr: string): string {
  const d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString('de-DE', { timeZone: 'Europe/Berlin', day: '2-digit', month: '2-digit', year: 'numeric' });
}

// =============================================================================
// COMPONENT
// =============================================================================

export default function WashPerformancePage() {
  const [searchParams, setSearchParams] = useSearchParams();

  // URL-State Persistence
  const initialPreset = (searchParams.get('preset') as Preset) || 'today';
  const [preset, setPreset] = useState<Preset>(
    ['today', 'yesterday', 'dayBefore', 'thisWeek', 'lastWeek', 'thisMonth', 'lastMonth', 'custom'].includes(initialPreset)
      ? initialPreset
      : 'today'
  );

  // Sync preset to URL
  useEffect(() => {
    if (preset !== 'custom') {
      setSearchParams({ preset }, { replace: true });
    }
  }, [preset, setSearchParams]);

  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  
  // Computed range (single source of truth)
  const range = useMemo(() => computeRange(preset, customStart, customEnd), [preset, customStart, customEnd]);

  // Partial-Range Info: WARN vs INFO vs none (UI-spezifisch, nicht aus shared.isPartial)
  // WARN: Zeitraum nicht abgeschlossen (thisWeek/thisMonth)
  // INFO: Intraday (today) - neutral, kein Warncharakter
  // none: Abgeschlossene Zeiträume
  const partialRangeInfo = useMemo(() => {
    const today = getTodayBerlin();
    
    // today = INFO (laufender Tag, neutral)
    if (preset === 'today') {
      return { type: 'info' as const, message: 'Stand: heute bis jetzt (laufender Tag)' };
    }
    
    // thisWeek/thisMonth = WARN (Zeitraum nicht abgeschlossen)
    if (preset === 'thisWeek' || preset === 'thisMonth') {
      return { 
        type: 'warn' as const, 
        message: `Zeitraum nicht abgeschlossen - Werte bis ${formatBerlinDateDE(today)} sind vorläufig.`
      };
    }
    
    // custom mit End >= today = WARN
    if (preset === 'custom' && customEnd && customEnd >= today) {
      return { 
        type: 'warn' as const, 
        message: `Zeitraum nicht abgeschlossen - Werte bis ${formatBerlinDateDE(today)} sind vorläufig.`
      };
    }
    
    // Abgeschlossene Zeiträume: kein Banner
    return { type: 'none' as const, message: '' };
  }, [preset, customEnd]);

  const isPartialRange = partialRangeInfo.type !== 'none';
  
  // Report data
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // UI state
  const [expandedDays, setExpandedDays] = useState<Set<string>>(new Set());
  
  // Load data - maps to Rebuild API
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Call Rebuild API with detail=shift to get shift-level data
      const url = `/api/reports/wash-performance?range=custom&start=${range.start}&end=${range.end}&detail=shift`;
      const res = await fetch(url, { credentials: 'include' });
      
      if (!res.ok) {
        if (res.status === 401) {
          window.location.href = '/login';
          return;
        }
        throw new Error('Fehler beim Laden der Daten');
      }
      
      const data = await res.json();
      
      // Transform Rebuild response to Legacy format
      const days = transformShiftsToLegacyFormat(data.shifts || []);
      const totals = calculateTotals(days, data.totals);
      
      setReportData({
        days,
        totals,
        rangeLabel: range.label,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unbekannter Fehler');
    }
    
    setLoading(false);
  }, [range]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Transform Rebuild shifts array to Legacy day-grouped format
  function transformShiftsToLegacyFormat(shifts: Array<{
    date: string;
    shiftType: ShiftType;
    wsaKg: number;
    flKg: number;
    p25: number;
    p50: number;
    wlKg: number;
    hbwKg: number;
    missingP25: boolean;
    missingP50: boolean;
  }>): DayReport[] {
    const grouped: Record<string, DayReport> = {};

    for (const shift of shifts) {
      if (!grouped[shift.date]) {
        grouped[shift.date] = {
          date: shift.date,
          wsaKg: 0,
          flKg: 0,
          p25Kg: 0,
          p50Kg: 0,
          wlKg: 0,
          hbwKg: 0,
          shifts: [],
          missingP25: false,
          missingP50: false,
        };
      }

      // Eine Anlage ohne Daten liefert 0 kg — alles wird normal summiert.
      const day = grouped[shift.date];
      day.wsaKg += shift.wsaKg;
      day.flKg += shift.flKg;
      day.hbwKg += shift.hbwKg ?? 0;
      day.p25Kg += shift.p25;
      day.p50Kg += shift.p50;
      day.wlKg += shift.wlKg;
      day.missingP25 = day.missingP25 || shift.missingP25;
      day.missingP50 = day.missingP50 || shift.missingP50;

      day.shifts.push({
        shiftDate: shift.date,
        shiftType: shift.shiftType,
        wsaKg: shift.wsaKg,
        flKg: shift.flKg,
        p25: shift.p25,
        p50: shift.p50,
        wlKg: shift.wlKg,
        hbwKg: shift.hbwKg ?? 0,
        missingP25: shift.missingP25,
        missingP50: shift.missingP50,
      });
    }

    // Sort by date descending (newest first)
    return Object.values(grouped).sort((a, b) => b.date.localeCompare(a.date));
  }

  function calculateTotals(days: DayReport[], apiTotals: Partial<ReportTotals> | undefined): ReportTotals {
    const summe = (f: (d: DayReport) => number) => days.reduce((a, d) => a + f(d), 0);
    return {
      wsaKg: apiTotals?.wsaKg ?? summe((d) => d.wsaKg),
      flKg: apiTotals?.flKg ?? summe((d) => d.flKg),
      p25Kg: apiTotals?.p25Kg ?? summe((d) => d.p25Kg),
      p50Kg: apiTotals?.p50Kg ?? summe((d) => d.p50Kg),
      wlKg: apiTotals?.wlKg ?? summe((d) => d.wlKg),
      hbwKg: apiTotals?.hbwKg ?? summe((d) => d.hbwKg),
      shiftsMissingP25: apiTotals?.shiftsMissingP25 ?? 0,
      shiftsMissingP50: apiTotals?.shiftsMissingP50 ?? 0,
    };
  }

  // Preset change handler
  const applyPreset = (newPreset: Preset) => {
    setPreset(newPreset);
    if (newPreset !== 'custom') {
      setCustomStart('');
      setCustomEnd('');
    }
  };

  // Toggle day expansion
  const toggleExpandDay = (date: string) => {
    setExpandedDays(prev => {
      const next = new Set(prev);
      if (next.has(date)) {
        next.delete(date);
      } else {
        next.add(date);
      }
      return next;
    });
  };

  // Format helpers
  const formatValue = (val: number): string => val.toLocaleString('de-DE') + ' kg';

  /** kg einer Taktwaschanlage; ohne Maschinendaten grau, mit Sternchen und Erklärung. */
  const twaWert = (kg: number, ohneDaten: boolean) => {
    if (!ohneDaten) return formatValue(kg);
    const erklaerung =
      kg === 0
        ? 'Keine Maschinendaten — als 0 kg gezählt'
        : 'Mindestens eine Schicht ohne Maschinendaten — dort als 0 kg gezählt';
    return (
      <span className="text-slate-400" title={erklaerung}>
        {formatValue(kg)}*<span className="sr-only"> ({erklaerung})</span>
      </span>
    );
  };

  const formatDate = (dateStr: string): string => {
    const d = new Date(dateStr + 'T12:00:00');
    return d.toLocaleDateString('de-DE', { timeZone: 'Europe/Berlin', weekday: 'short', day: '2-digit', month: '2-digit' });
  };

  // Export
  const exportData = (format: 'xlsx' | 'csv') => {
    const mode = format === 'xlsx' ? 'shifts' : 'summary';
    window.location.href = `/api/reports/wash-performance/export?range=custom&start=${range.start}&end=${range.end}&mode=${mode}`;
  };

  // KPIs from report data
  const kpis = reportData ? {
    wsa: reportData.totals.wsaKg,
    p25: reportData.totals.p25Kg,
    p50: reportData.totals.p50Kg,
    wl: reportData.totals.wlKg,
    fl: reportData.totals.flKg,
    hbw: reportData.totals.hbwKg,
    missingP25: reportData.totals.shiftsMissingP25 > 0,
    missingP50: reportData.totals.shiftsMissingP50 > 0,
  } : null;

  const schichten = (n: number) => (n === 1 ? '1 Schicht' : `${n} Schichten`);
  const luecken = reportData
    ? [
        reportData.totals.shiftsMissingP25 > 0 && `P25 in ${schichten(reportData.totals.shiftsMissingP25)}`,
        reportData.totals.shiftsMissingP50 > 0 && `P50 in ${schichten(reportData.totals.shiftsMissingP50)}`,
      ].filter(Boolean)
    : [];

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* ================================================================
            HEADER (Legacy: lines 303-321)
            ================================================================ */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Waschleistung</h1>
            <p className="text-sm text-slate-500">Waschleistung = WSA + P25 + P50</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={loadData}
              disabled={loading}
              className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              title="Daten aktualisieren"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <ExportButtonsPlaceholder />
          </div>
        </div>

        {/* ================================================================
            TIME FILTER - PRESET BUTTONS (Legacy: lines 324-417)
            ================================================================ */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 space-y-4">
          {/* Preset Buttons */}
          <div className="flex flex-wrap gap-2">
            {(['today', 'yesterday', 'dayBefore', 'thisWeek', 'lastWeek', 'thisMonth', 'lastMonth'] as Preset[]).map((p) => (
              <button
                key={p}
                onClick={() => applyPreset(p)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  preset === p
                    ? 'bg-blue-600 text-white'
                    : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                {PRESET_LABELS[p]}
              </button>
            ))}
            <div className="border-l border-gray-200 mx-1" />
            <button
              onClick={() => applyPreset('custom')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                preset === 'custom'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              {PRESET_LABELS.custom}
            </button>
          </div>

          {/* Custom date pickers + Range label */}
          <div className="flex flex-wrap gap-4 items-center pt-2 border-t border-gray-100">
            {preset === 'custom' && (
              <div className="flex items-center gap-2">
                <div>
                  <label className="text-xs text-slate-500 block mb-1">Von</label>
                  <input
                    type="date"
                    value={customStart}
                    onChange={(e) => setCustomStart(e.target.value)}
                    className="w-40 px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-500 block mb-1">Bis</label>
                  <input
                    type="date"
                    value={customEnd}
                    onChange={(e) => setCustomEnd(e.target.value)}
                    className="w-40 px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
              </div>
            )}
            
            {/* Range Label */}
            <div className="flex items-center gap-2 text-sm text-slate-700 bg-slate-50 px-4 py-2.5 rounded-md border border-slate-200">
              <Calendar className="w-4 h-4 text-slate-400" />
              <span className="font-medium">{getRangeLabelConcrete(preset, range.start, range.end)}</span>
            </div>
          </div>
        </div>

        {/* ================================================================
            WARNING CARD (Legacy: lines 419-437)
            ================================================================ */}
        {luecken.length > 0 && (
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 flex items-start gap-3">
            <Info className="w-5 h-5 text-slate-500 shrink-0 mt-0.5" />
            <p className="text-sm text-slate-600">
              * Ohne Maschinendaten, als 0 kg gezählt: {luecken.join(', ')}. Das ist ein Stillstand
              der Anlage oder ein Datenausfall.
            </p>
          </div>
        )}

        {/* Hinweis für offene Zeiträume: WARN (amber) vs INFO (blue) */}
        {partialRangeInfo.type === 'warn' && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-800">Zeitraum nicht abgeschlossen</p>
              <p className="text-sm text-amber-700">{partialRangeInfo.message}</p>
            </div>
          </div>
        )}
        {partialRangeInfo.type === 'info' && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
            <p className="text-sm text-blue-700">{partialRangeInfo.message}</p>
          </div>
        )}

        {/* ================================================================
            KPI CARDS - 5 columns (Legacy: lines 439-490)
            ================================================================ */}
        {kpis && (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
            {/* WSA */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
              <div className="flex items-center gap-2 text-slate-500 text-sm">
                <WashingMachine className="w-4 h-4" />
                WSA
                {isPartialRange && <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">vorläufig</span>}
              </div>
              <p className="text-2xl font-bold text-slate-900">{formatValue(kpis.wsa)}</p>
            </div>
            
            {/* P25 */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
              <div className="flex items-center gap-2 text-slate-500 text-sm">P25</div>
              <p className="text-2xl font-bold text-slate-900">{twaWert(kpis.p25, kpis.missingP25)}</p>
            </div>
            
            {/* P50 */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
              <div className="flex items-center gap-2 text-slate-500 text-sm">P50</div>
              <p className="text-2xl font-bold text-slate-900">{twaWert(kpis.p50, kpis.missingP50)}</p>
            </div>

            {/* HBW */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
              <div className="flex items-center gap-2 text-slate-500 text-sm">
                HBW
                {isPartialRange && <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">vorläufig</span>}
              </div>
              <p className="text-2xl font-bold text-slate-900">{formatValue(kpis.hbw)}</p>
            </div>
            
            {/* Waschleistung */}
            <div className="rounded-xl shadow-sm border p-4 border-blue-200 bg-blue-50">
              <div className="flex items-center gap-2 text-slate-500 text-sm">
                <TrendingUp className="w-4 h-4" />
                Waschleistung
                {isPartialRange && <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">vorläufig</span>}
              </div>
              <p className="text-2xl font-bold text-blue-600">{formatValue(kpis.wl)}</p>
            </div>
            
            {/* Fertigleistung */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
              <div className="flex items-center gap-2 text-slate-500 text-sm">
                <Scale className="w-4 h-4" />
                Fertigleistung
                {isPartialRange && <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">vorläufig</span>}
              </div>
              <p className="text-2xl font-bold text-slate-900">{formatValue(kpis.fl)}</p>
            </div>
          </div>
        )}

        {/* ================================================================
            TABLE (Legacy: lines 492-607)
            ================================================================ */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="py-8 text-center text-slate-500">Daten werden geladen...</div>
          ) : error ? (
            <div className="py-8 text-center text-red-500">{error}</div>
          ) : reportData && reportData.days.length > 0 ? (
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="w-10 px-4 py-3"></th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Datum</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">WSA</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">P25</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">P50</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">HBW</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Waschleistung</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Fertigleistung</th>
                  <th className="px-4 py-3 text-xs font-medium text-slate-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {reportData.days
                  .filter(day => day.shifts.length > 0)
                  .map((day) => (
                    <Fragment key={day.date}>
                      {/* Day Row */}
                      <tr
                        className="cursor-pointer hover:bg-slate-50 transition-colors"
                        onClick={() => toggleExpandDay(day.date)}
                      >
                        <td className="px-4 py-3">
                          {expandedDays.has(day.date)
                            ? <ChevronDown className="w-4 h-4 text-slate-400" />
                            : <ChevronRight className="w-4 h-4 text-slate-400" />
                          }
                        </td>
                        <td className="px-4 py-3 font-medium text-slate-900">{formatDate(day.date)}</td>
                        <td className="px-4 py-3 text-right text-slate-900">{formatValue(day.wsaKg)}</td>
                        <td className="px-4 py-3 text-right text-slate-900">{twaWert(day.p25Kg, day.missingP25)}</td>
                        <td className="px-4 py-3 text-right text-slate-900">{twaWert(day.p50Kg, day.missingP50)}</td>
                        <td className="px-4 py-3 text-right text-slate-900">{formatValue(day.hbwKg ?? 0)}</td>
                        <td className="px-4 py-3 text-right font-medium text-blue-600">{formatValue(day.wlKg)}</td>
                        <td className="px-4 py-3 text-right text-slate-900">{formatValue(day.flKg)}</td>
                        <td className="px-4 py-3"></td>
                      </tr>
                      
                      {/* Shift Drilldown - PR-5: Edit Button (Gap 19) */}
                      {expandedDays.has(day.date) && day.shifts.map((shift) => (
                        <tr key={`${day.date}-${shift.shiftType}`} className="bg-slate-50 text-sm">
                          <td className="px-4 py-2"></td>
                          <td className="px-4 py-2 pl-8 text-slate-500">
                            <span className={SHIFT_BADGE_CLASS[shift.shiftType] ?? 'inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-slate-100 text-slate-700'}>
                              {SHIFT_LABELS[shift.shiftType]}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-right text-slate-600">{formatValue(shift.wsaKg)}</td>
                          <td className="px-4 py-2 text-right text-slate-600">{twaWert(shift.p25, shift.missingP25)}</td>
                          <td className="px-4 py-2 text-right text-slate-600">{twaWert(shift.p50, shift.missingP50)}</td>
                          <td className="px-4 py-2 text-right text-slate-600">{formatValue(shift.hbwKg ?? 0)}</td>
                          <td className="px-4 py-2 text-right text-slate-600">{formatValue(shift.wlKg)}</td>
                          <td className="px-4 py-2 text-right text-slate-600">{formatValue(shift.flKg)}</td>
                          <td className="px-4 py-2"></td>
                        </tr>
                      ))}
                    </Fragment>
                  ))}
                
                {/* SUMME Row */}
                <tr className="bg-blue-50 font-medium border-t-2 border-blue-200">
                  <td className="px-4 py-3"></td>
                  <td className="px-4 py-3 text-slate-900">SUMME</td>
                  <td className="px-4 py-3 text-right text-slate-900">{formatValue(reportData.totals.wsaKg)}</td>
                  <td className="px-4 py-3 text-right text-slate-900">
                    {twaWert(reportData.totals.p25Kg, reportData.totals.shiftsMissingP25 > 0)}
                  </td>
                  <td className="px-4 py-3 text-right text-slate-900">
                    {twaWert(reportData.totals.p50Kg, reportData.totals.shiftsMissingP50 > 0)}
                  </td>
                  <td className="px-4 py-3 text-right text-slate-900">{formatValue(reportData.totals.hbwKg ?? 0)}</td>
                  <td className="px-4 py-3 text-right text-blue-600">{formatValue(reportData.totals.wlKg)}</td>
                  <td className="px-4 py-3 text-right text-slate-900">{formatValue(reportData.totals.flKg)}</td>
                  <td className="px-4 py-3"></td>
                </tr>
              </tbody>
            </table>
          ) : (
            <div className="py-8 text-center text-slate-500">Keine Daten vorhanden</div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
